import axios from "axios";
import { get } from "lodash";
import { logout } from "../app/login/duck/actions/login";
import config from "../config/index";
import { store } from "../store";
import ApiError from "../util/ApiError";
import { logError } from "../util/logging";
import { errors as codes } from "@chaser/codes";
import { toast } from "../components/ToastContainer";
import reportApiError from "./reportApiError";

// Set default axios configuration
axios.defaults.withCredentials = true;

/**
 * Handle permission denied errors consistently
 * @param {Object} error - The error object from axios
 * @param {Object} data - The error data from the response
 * @returns {Promise} - A rejected promise with the appropriate ApiError
 */
const handlePermissionDenied = (error, data) => {
  logError(
    "403: User missing required permission",
    { error, time: new Date().toISOString() },
    "info",
  );
  return Promise.reject(
    new ApiError("This action needs additional permissions.", data),
  );
};

/**
 * Handle user logout consistently
 * @param {Object} error - The error object from axios
 * @param {number} status - The HTTP status code
 * @param {boolean} redirectToLogin - Whether to redirect to login page
 * @returns {Promise} - A rejected promise with the appropriate ApiError
 */
const handleUserLogout = (error, status, redirectToLogin = false) => {
  store.dispatch(logout(undefined, { redirectToLogin }));
  logError(
    "403: User logged out",
    { error, time: new Date().toISOString() },
    "info",
  );
  return Promise.reject(new ApiError("You have been logged out", status));
};

/**
 * Process API errors from response data
 * @param {Object} response - The axios response object
 * @param {string} errorMessage - Optional custom error message
 * @throws {ApiError} - Throws an ApiError with appropriate details
 */
const processApiErrors = (response, errorMessage) => {
  const { data: result } = response;
  const { data, errors } = result;

  if (!(errors && errors.length)) return data;

  const message = errorMessage || get(errors, "0.message");
  const info = {
    data: errors,
    query: response.config.data,
  };

  if (errors && errors.length) {
    if (message[0] === "{") {
      let code, meta;
      try {
        ({ code, meta } = JSON.parse(message));
      } catch (e) {
        throw new ApiError(message, {
          info: {
            data: errors,
            query: response.config.data,
          },
        });
      }
      throw new ApiError(null, {
        log: false,
        code,
        meta,
        info: {
          data: errors,
          query: response.config.data,
        },
      });
    }
  }

  throw new ApiError(message, { info });
};

/**
 * Create an axios instance with interceptors for a specific API path
 * @param {string} path - The API path to append to the base URL
 * @param {Object} options - Configuration options
 * @returns {Object} - Configured axios instance
 */
function create(
  path,
  {
    errorMessage,
    headers: headerOverrides,
    logoutWhenUnauthorized = true,
    extractResponseData = true,
  } = {},
) {
  const { token } = store.getState().loginReducer;
  const baseURL = `${config.apiUrl}${path}`;
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  Object.assign(headers, headerOverrides);

  const instance = axios.create({ baseURL, headers });

  instance.interceptors.response.use(
    (response) => {
      if (response.status === 202) {
        const message =
          response.data?.message ||
          "The update is running. You will receive an email when it’s complete.";
        toast.info(message, { autoClose: 5000 });
      }

      if (!extractResponseData) {
        return response;
      }
      return processApiErrors(response, errorMessage);
    },
    (error) => {
      const response = error?.response || {};
      const { status, data } = response;

      if (status === 403 && logoutWhenUnauthorized) {
        if (data.extensions?.code === codes.USER_PERMISSION_DENIED) {
          return handlePermissionDenied(error, data);
        }
        return handleUserLogout(error, status, true);
      }

      error.info = {
        data: get(response, "data.errors"),
        query: get(response, "config.data"),
      };

      reportApiError(error, status);

      throw error;
    },
  );

  return instance;
}

/**
 * API Service with endpoint creation methods
 */
const ApiService = {
  /**
   * Create an endpoint for a specific organization
   * @param {string} ShortCode - Organization short code
   * @param {Object} options - Configuration options
   * @returns {Object} - Configured axios instance
   */
  orgEndpoint(ShortCode, options) {
    if (!ShortCode) {
      throw new Error("ApiService: ShortCode was not provided to orgEndpoint");
    }
    return create(`/${ShortCode}`, options);
  },

  /**
   * Create an endpoint for user-specific operations
   * @param {Object} options - Configuration options
   * @returns {Object} - Configured axios instance
   */
  meEndpoint(options) {
    return create("/me", options);
  },

  /**
   * Create an endpoint for base API operations
   * @param {Object} options - Configuration options
   * @returns {Object} - Configured axios instance
   */
  baseEndpoint(options) {
    return create("/", options);
  },

  /**
   * Create an endpoint for file downloads
   * @param {string} path - Path to the file
   * @returns {Promise} - Promise that resolves with the file blob
   */
  fileDownload(path) {
    return create("/", { extractResponseData: false }).get(path, {
      responseType: "blob",
    });
  },
};

export default ApiService;
